import { formatDateLabel, formatNumberLabel, toArray, toNumberValue, toStringValue, toTimestamp } from '@/features/charting-next/adapters/chartingAdapterUtils';

const REGISTRY_TYPE_LABELS = {
    favorite: 'Favorito',
    note: 'Nota',
    alarm: 'Alarma'
};

export function normalizeChartingAlerts(payload = [], ticker = '') {
    const normalizedTicker = toStringValue(ticker).toUpperCase();

    return toArray(payload)
        .filter((item) => {
            if (!normalizedTicker) {
                return true;
            }

            const itemTicker = toStringValue(item?.ticker_name || item?.ticker).toUpperCase();

            return itemTicker === normalizedTicker;
        })
        .map((item, index) => {
            const createdTimestamp = toTimestamp(item?.created || item?.timestamp);
            const executedTimestamp = toTimestamp(item?.executed_date || item?.modified);
            const status = toNumberValue(item?.status, 0);

            return {
                id: toStringValue(item?.id, `alert-${index}`),
                ticker: toStringValue(item?.ticker_name || item?.ticker).toUpperCase(),
                field: toStringValue(item?.alert_field),
                fieldLabel: toStringValue(item?.alert_field_string || item?.alert_field),
                mode: toStringValue(item?.alert_mode),
                modeLabel: toStringValue(item?.alert_mode_string || item?.alert_mode),
                value: formatNumberLabel(item?.alert_data) || toStringValue(item?.alert_data),
                status,
                isExecuted: status === 1,
                createdAt: createdTimestamp,
                createdLabel: formatDateLabel(createdTimestamp),
                executedAt: executedTimestamp,
                executedLabel: formatDateLabel(executedTimestamp),
                raw: item
            };
        })
        .sort((left, right) => (right.createdAt || 0) - (left.createdAt || 0));
}

export function normalizeChartingRegistry(payload = {}, ticker = '') {
    const normalizedTicker = toStringValue(ticker).toUpperCase();
    const registry = payload?.registry || {};
    const notes = toArray(payload?.notes);
    const alerts = normalizeChartingAlerts(payload?.alerts || [], normalizedTicker);
    const alertIndex = Object.fromEntries(alerts.map((item) => [item.id, item]));
    const items = [];

    toArray(registry?.favorites).forEach((item, index) => {
        const itemTicker = toStringValue(item?.ticker).toUpperCase();

        if (normalizedTicker && itemTicker !== normalizedTicker) {
            return;
        }

        const folderName = toStringValue(item?.favorite_folder_name, 'Favoritos');
        const favoriteAction = toStringValue(item?.favorite_action);

        items.push({
            id: `favorite-${index}-${toStringValue(item?.timestamp)}`,
            type: 'favorite',
            typeLabel: REGISTRY_TYPE_LABELS.favorite,
            createdAt: toTimestamp(item?.timestamp),
            createdLabel: formatDateLabel(item?.timestamp),
            text: favoriteAction === 'FAVORITE_REMOVED' ? `Ticker ${itemTicker} eliminado de la carpeta ${folderName}.` : `Ticker ${itemTicker} añadido a la carpeta ${folderName}.`,
            raw: item
        });
    });

    notes.forEach((item, index) => {
        const itemTicker = toStringValue(item?.ticker).toUpperCase();

        if (normalizedTicker && itemTicker !== normalizedTicker) {
            return;
        }

        items.push({
            id: `note-${toStringValue(item?.nid, index)}`,
            type: 'note',
            typeLabel: REGISTRY_TYPE_LABELS.note,
            createdAt: toTimestamp(item?.timestamp),
            createdLabel: formatDateLabel(item?.timestamp),
            text: toStringValue(item?.body),
            noteId: toStringValue(item?.nid),
            raw: item
        });
    });

    toArray(registry?.alerts).forEach((item, index) => {
        const itemTicker = toStringValue(item?.ticker).toUpperCase();

        if (normalizedTicker && itemTicker !== normalizedTicker) {
            return;
        }

        const alertId = toStringValue(item?.alert_id);
        const alertDetail = alertIndex[alertId];
        const isExecuted = toStringValue(item?.alert_action) === 'ALERT_EXECUTED';
        const fallbackText = isExecuted ? `Alarma ejecutada para ${itemTicker}.` : `Alarma creada para ${itemTicker}.`;

        items.push({
            id: `alert-${index}-${alertId || toStringValue(item?.timestamp)}`,
            type: 'alarm',
            typeLabel: REGISTRY_TYPE_LABELS.alarm,
            createdAt: toTimestamp(item?.timestamp),
            createdLabel: formatDateLabel(item?.timestamp),
            text: alertDetail ? `La alarma ${alertDetail.fieldLabel} ${alertDetail.modeLabel} ${alertDetail.value} ${isExecuted ? 'ha sido ejecutada' : 'ha sido creada'}.` : fallbackText,
            raw: item
        });
    });

    return items.sort((left, right) => (right.createdAt || 0) - (left.createdAt || 0));
}
